import { ListAPIResponseBase } from 'types/frontend';
import {
  canAdminEntity,
  canEditEntity,
  canPublishEntity,
  shouldApplyGranularPermissions,
} from './GranularPermissions';

export interface EntityListPermissions {
  getCanArchive: (key: string) => boolean;
  getCanCreate: () => boolean;
  getCanDeleteItem: (key: string) => boolean;
  getCanManagePermissions: (key: string) => boolean;
  getCanUnarchive: (key: string) => boolean;
  getCanUpdate: (key: string) => boolean;
}

export interface EntityListPermissionsConfig<T> {
  doesRequireStrictGranularPermissions?: boolean;
  isGranularPermissionsEnabled?: boolean;
  latestApiResponse?: T;
  resolvedApiResponse?: T;
}

function canUpdateEntityWithGranularPermissions(
  role: string | undefined,
  doesRequireStrictGranularPermissions: boolean
): boolean {
  if (doesRequireStrictGranularPermissions) {
    // some entities require strict control for delete/archive/publish permissions
    // - Reports, etc
    return canPublishEntity(role);
  } else {
    return canEditEntity(role);
  }
}

export function getEntityListPermissions<T extends ListAPIResponseBase>({
  doesRequireStrictGranularPermissions = false,
  isGranularPermissionsEnabled = false,
  latestApiResponse,
  resolvedApiResponse,
}: EntityListPermissionsConfig<T>): EntityListPermissions {
  return {
    getCanArchive: (key: string): boolean => {
      if (!latestApiResponse) {
        return false;
      }
      const item = latestApiResponse.items.find(
        item => item.key === key || item.id?.toString() === key
      );

      if (shouldApplyGranularPermissions(isGranularPermissionsEnabled, item)) {
        return canUpdateEntityWithGranularPermissions(
          item?.role,
          doesRequireStrictGranularPermissions
        );
      } else {
        return !!(item && item.archive_url);
      }
    },

    getCanCreate: (): boolean => {
      // For flag creation, we do not need extra control for granular permissions
      return !!(resolvedApiResponse && resolvedApiResponse.create_url);
    },

    getCanDeleteItem: (key: string): boolean => {
      if (!latestApiResponse) {
        return false;
      }
      const item = latestApiResponse.items.find(
        item => item.key === key || item.id?.toString() === key
      );

      if (shouldApplyGranularPermissions(isGranularPermissionsEnabled, item)) {
        return canUpdateEntityWithGranularPermissions(
          item?.role,
          doesRequireStrictGranularPermissions
        );
      } else {
        return !!(item && item.delete_url);
      }
    },

    getCanManagePermissions: (key: string): boolean => {
      if (!latestApiResponse) {
        return false;
      }
      const item = latestApiResponse.items.find(
        item => item.key === key || item.id?.toString() === key
      );

      if (shouldApplyGranularPermissions(isGranularPermissionsEnabled, item)) {
        return canAdminEntity(item?.role);
      } else {
        return false;
      }
    },

    getCanUnarchive: (key: string): boolean => {
      if (!latestApiResponse) {
        return false;
      }
      const item = latestApiResponse.items.find(
        item => item.key === key || item.id?.toString() === key
      );

      if (shouldApplyGranularPermissions(isGranularPermissionsEnabled, item)) {
        return canUpdateEntityWithGranularPermissions(
          item?.role,
          doesRequireStrictGranularPermissions
        );
      } else {
        return !!(item && item.unarchive_url);
      }
    },

    getCanUpdate: (key: string): boolean => {
      if (!latestApiResponse) {
        return false;
      }
      const item = latestApiResponse.items.find(
        item => item.key === key || item.id?.toString() === key
      );

      if (shouldApplyGranularPermissions(isGranularPermissionsEnabled, item)) {
        return canEditEntity(item?.role);
      } else {
        return !!(item && item.update_url);
      }
    },
  };
}
